import { RefObject, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { selectOneSetting } from '../../util/store/selectors';
import { useSelector } from '../../util/store/store';
import { OverviewSetting } from '../../util/varibles/userSetting';
import { loginActions } from '../LoginPage/reducer';

const ZOOM_LEVELS = [4, 3, 2, 1];
const TIMELINE_LEFT_OFFSET = 160;
export const TIMELINE_ZOOM_EVENT = 'overview-timeline-zoom';

const useTimelineZoom = (calendarRef: RefObject<HTMLDivElement>) => {
    const dispatch = useDispatch();
    const weekPerPage = useSelector(selectOneSetting(OverviewSetting.WEEK_PER_PAGE));
    const weekPerPageRef = useRef(weekPerPage);
    const anchorRef = useRef<{ scaledPosition: number, pointerX: number } | null>(null);
    const restoreTimerRef = useRef<number>();
    const resetAnchorTimerRef = useRef<number>();
    const frameRef = useRef<number>();
    weekPerPageRef.current = weekPerPage;

    useEffect(() => {
        const calendar = calendarRef.current;
        if (!calendar)
            return;

        const restorePointerPosition = () => {
            const anchor = anchorRef.current;
            if (!anchor)
                return;

            const nextScrollLeft = anchor.scaledPosition / weekPerPageRef.current - anchor.pointerX;
            const maxScrollLeft = Math.max(calendar.scrollWidth - calendar.clientWidth, 0);
            calendar.scrollLeft = Math.min(Math.max(nextScrollLeft, 0), maxScrollLeft);
        };

        const applyZoom = (nextWeekPerPage: number, pointerX: number, resetAnchor = false) => {
            if (resetAnchor || !anchorRef.current) {
                anchorRef.current = {
                    scaledPosition: (calendar.scrollLeft + pointerX) * weekPerPageRef.current,
                    pointerX,
                };
            } else {
                anchorRef.current.pointerX = pointerX;
            }

            weekPerPageRef.current = nextWeekPerPage;
            dispatch(loginActions.updateUserSetting([{
                [OverviewSetting.WEEK_PER_PAGE]: nextWeekPerPage,
            }]));

            if (frameRef.current)
                cancelAnimationFrame(frameRef.current);
            frameRef.current = requestAnimationFrame(() => {
                frameRef.current = requestAnimationFrame(restorePointerPosition);
            });

            window.clearTimeout(restoreTimerRef.current);
            restoreTimerRef.current = window.setTimeout(restorePointerPosition, 140);
            window.clearTimeout(resetAnchorTimerRef.current);
            resetAnchorTimerRef.current = window.setTimeout(() => {
                anchorRef.current = null;
            }, 220);
        };

        const handleWheel = (event: WheelEvent) => {
            if ((!event.ctrlKey && !event.metaKey) || event.deltaY === 0)
                return;

            const { left, right } = calendar.getBoundingClientRect();
            if (event.clientX < left + TIMELINE_LEFT_OFFSET || event.clientX > right)
                return;

            const currentWeekPerPage = weekPerPageRef.current;
            const currentIndex = ZOOM_LEVELS.indexOf(currentWeekPerPage);
            const safeIndex = currentIndex === -1 ? 0 : currentIndex;
            const nextIndex = Math.min(
                ZOOM_LEVELS.length - 1,
                Math.max(0, safeIndex + (event.deltaY < 0 ? 1 : -1))
            );
            const nextWeekPerPage = ZOOM_LEVELS[nextIndex];

            event.preventDefault();
            if (nextWeekPerPage !== currentWeekPerPage) {
                const pointerX = event.clientX - left - TIMELINE_LEFT_OFFSET;
                applyZoom(nextWeekPerPage, pointerX);
            }
        };

        const handleOptionZoom = (event: Event) => {
            const nextWeekPerPage = (event as CustomEvent<{ weekPerPage: number }>).detail.weekPerPage;
            if (nextWeekPerPage !== weekPerPageRef.current)
                applyZoom(nextWeekPerPage, 0, true);
        };

        calendar.addEventListener('wheel', handleWheel, { passive: false });
        calendar.addEventListener(TIMELINE_ZOOM_EVENT, handleOptionZoom);
        return () => {
            calendar.removeEventListener('wheel', handleWheel);
            calendar.removeEventListener(TIMELINE_ZOOM_EVENT, handleOptionZoom);
            window.clearTimeout(restoreTimerRef.current);
            window.clearTimeout(resetAnchorTimerRef.current);
            if (frameRef.current)
                cancelAnimationFrame(frameRef.current);
        };
    }, [calendarRef, dispatch]);
};

export default useTimelineZoom;
